import type { Language } from "./translations";

export type LegalTranslationKeys = {
  // Cookie banner
  cookie_banner_title: string;
  cookie_banner_desc: string;
  cookie_accept_all: string;
  cookie_reject: string;
  cookie_configure: string;
  cookie_settings_title: string;
  cookie_technical: string;
  cookie_technical_desc: string;
  cookie_analytical: string;
  cookie_analytical_desc: string;
  cookie_advertising: string;
  cookie_advertising_desc: string;
  cookie_always_active: string;
  cookie_save: string;
  cookie_manage: string;
  // Footer legal
  footer_legal_notice: string;
  footer_privacy_policy: string;
  footer_cookie_policy: string;
  // SEO legal
  seo_legal_title: string;
  seo_legal_desc: string;
  seo_privacy_title: string;
  seo_privacy_desc: string;
  seo_cookies_title: string;
  seo_cookies_desc: string;
};

const es: LegalTranslationKeys = {
  cookie_banner_title: "Utilizamos cookies",
  cookie_banner_desc: "Usamos cookies para mejorar tu experiencia. Puedes aceptar todas, rechazar las no esenciales o configurar tus preferencias.",
  cookie_accept_all: "Aceptar todas",
  cookie_reject: "Rechazar",
  cookie_configure: "Configurar",
  cookie_settings_title: "Configuración de cookies",
  cookie_technical: "Cookies técnicas",
  cookie_technical_desc: "Necesarias para el funcionamiento básico del sitio web. No se pueden desactivar.",
  cookie_analytical: "Cookies analíticas",
  cookie_analytical_desc: "Nos permiten analizar el uso del sitio web para mejorar su rendimiento y contenido.",
  cookie_advertising: "Cookies publicitarias",
  cookie_advertising_desc: "Utilizadas para mostrar anuncios relevantes según tus intereses.",
  cookie_always_active: "Siempre activas",
  cookie_save: "Guardar preferencias",
  cookie_manage: "Configurar cookies",
  footer_legal_notice: "Aviso Legal",
  footer_privacy_policy: "Política de Privacidad",
  footer_cookie_policy: "Política de Cookies",
  seo_legal_title: "Aviso Legal — Chibi Kokoro | Restaurante Japonés Barcelona",
  seo_legal_desc: "Aviso legal de CHIBIKOKORO S.L. Información sobre la empresa, propiedad intelectual, condiciones de uso y legislación aplicable.",
  seo_privacy_title: "Política de Privacidad — Chibi Kokoro | RGPD",
  seo_privacy_desc: "Política de privacidad de CHIBIKOKORO S.L. conforme al RGPD. Información sobre tratamiento de datos, derechos y seguridad.",
  seo_cookies_title: "Política de Cookies — Chibi Kokoro",
  seo_cookies_desc: "Política de cookies de Chibi Kokoro. Tipos de cookies utilizadas, finalidad y cómo gestionarlas.",
};

const en: LegalTranslationKeys = {
  cookie_banner_title: "We use cookies",
  cookie_banner_desc: "We use cookies to improve your experience. You can accept all, reject non-essential ones, or configure your preferences.",
  cookie_accept_all: "Accept all",
  cookie_reject: "Reject",
  cookie_configure: "Configure",
  cookie_settings_title: "Cookie settings",
  cookie_technical: "Technical cookies",
  cookie_technical_desc: "Required for the basic operation of the website. Cannot be disabled.",
  cookie_analytical: "Analytical cookies",
  cookie_analytical_desc: "Allow us to analyze website usage to improve performance and content.",
  cookie_advertising: "Advertising cookies",
  cookie_advertising_desc: "Used to show relevant ads based on your interests.",
  cookie_always_active: "Always active",
  cookie_save: "Save preferences",
  cookie_manage: "Cookie settings",
  footer_legal_notice: "Legal Notice",
  footer_privacy_policy: "Privacy Policy",
  footer_cookie_policy: "Cookie Policy",
  seo_legal_title: "Legal Notice — Chibi Kokoro | Japanese Restaurant Barcelona",
  seo_legal_desc: "Legal notice of CHIBIKOKORO S.L. Company information, intellectual property, terms of use and applicable law.",
  seo_privacy_title: "Privacy Policy — Chibi Kokoro | GDPR",
  seo_privacy_desc: "Privacy policy of CHIBIKOKORO S.L. in accordance with the GDPR. Data processing, rights and security.",
  seo_cookies_title: "Cookie Policy — Chibi Kokoro",
  seo_cookies_desc: "Chibi Kokoro cookie policy. Types of cookies used, purpose and how to manage them.",
};

const ca: LegalTranslationKeys = {
  cookie_banner_title: "Utilitzem cookies",
  cookie_banner_desc: "Utilitzem cookies per millorar la teva experiència. Pots acceptar-les totes, rebutjar les no essencials o configurar les teves preferències.",
  cookie_accept_all: "Acceptar totes",
  cookie_reject: "Rebutjar",
  cookie_configure: "Configurar",
  cookie_settings_title: "Configuració de cookies",
  cookie_technical: "Cookies tècniques",
  cookie_technical_desc: "Necessàries per al funcionament bàsic del lloc web. No es poden desactivar.",
  cookie_analytical: "Cookies analítiques",
  cookie_analytical_desc: "Ens permeten analitzar l'ús del lloc web per millorar-ne el rendiment i el contingut.",
  cookie_advertising: "Cookies publicitàries",
  cookie_advertising_desc: "Utilitzades per mostrar anuncis rellevants segons els teus interessos.",
  cookie_always_active: "Sempre actives",
  cookie_save: "Desar preferències",
  cookie_manage: "Configurar cookies",
  footer_legal_notice: "Avís Legal",
  footer_privacy_policy: "Política de Privacitat",
  footer_cookie_policy: "Política de Cookies",
  seo_legal_title: "Avís Legal — Chibi Kokoro | Restaurant Japonès Barcelona",
  seo_legal_desc: "Avís legal de CHIBIKOKORO S.L. Informació sobre l'empresa, propietat intel·lectual i condicions d'ús.",
  seo_privacy_title: "Política de Privacitat — Chibi Kokoro | RGPD",
  seo_privacy_desc: "Política de privacitat de CHIBIKOKORO S.L. conforme al RGPD.",
  seo_cookies_title: "Política de Cookies — Chibi Kokoro",
  seo_cookies_desc: "Política de cookies de Chibi Kokoro. Tipus de cookies, finalitat i gestió.",
};

const pt: LegalTranslationKeys = {
  cookie_banner_title: "Utilizamos cookies",
  cookie_banner_desc: "Utilizamos cookies para melhorar a sua experiência. Pode aceitar todas, rejeitar as não essenciais ou configurar as suas preferências.",
  cookie_accept_all: "Aceitar todas",
  cookie_reject: "Rejeitar",
  cookie_configure: "Configurar",
  cookie_settings_title: "Configuração de cookies",
  cookie_technical: "Cookies técnicos",
  cookie_technical_desc: "Necessários para o funcionamento básico do site. Não podem ser desativados.",
  cookie_analytical: "Cookies analíticos",
  cookie_analytical_desc: "Permitem analisar o uso do site para melhorar desempenho e conteúdo.",
  cookie_advertising: "Cookies publicitários",
  cookie_advertising_desc: "Utilizados para mostrar anúncios relevantes de acordo com os seus interesses.",
  cookie_always_active: "Sempre ativos",
  cookie_save: "Guardar preferências",
  cookie_manage: "Configurar cookies",
  footer_legal_notice: "Aviso Legal",
  footer_privacy_policy: "Política de Privacidade",
  footer_cookie_policy: "Política de Cookies",
  seo_legal_title: "Aviso Legal — Chibi Kokoro | Restaurante Japonês Barcelona",
  seo_legal_desc: "Aviso legal de CHIBIKOKORO S.L.",
  seo_privacy_title: "Política de Privacidade — Chibi Kokoro | RGPD",
  seo_privacy_desc: "Política de privacidade de CHIBIKOKORO S.L. conforme ao RGPD.",
  seo_cookies_title: "Política de Cookies — Chibi Kokoro",
  seo_cookies_desc: "Política de cookies de Chibi Kokoro.",
};

const fr: LegalTranslationKeys = {
  cookie_banner_title: "Nous utilisons des cookies",
  cookie_banner_desc: "Nous utilisons des cookies pour améliorer votre expérience. Vous pouvez accepter toutes, rejeter les non essentielles ou configurer vos préférences.",
  cookie_accept_all: "Accepter toutes",
  cookie_reject: "Rejeter",
  cookie_configure: "Configurer",
  cookie_settings_title: "Paramètres des cookies",
  cookie_technical: "Cookies techniques",
  cookie_technical_desc: "Nécessaires au fonctionnement de base du site. Ne peuvent pas être désactivés.",
  cookie_analytical: "Cookies analytiques",
  cookie_analytical_desc: "Nous permettent d'analyser l'utilisation du site pour améliorer ses performances.",
  cookie_advertising: "Cookies publicitaires",
  cookie_advertising_desc: "Utilisés pour afficher des publicités pertinentes selon vos intérêts.",
  cookie_always_active: "Toujours actifs",
  cookie_save: "Enregistrer les préférences",
  cookie_manage: "Paramètres cookies",
  footer_legal_notice: "Mentions légales",
  footer_privacy_policy: "Politique de confidentialité",
  footer_cookie_policy: "Politique de cookies",
  seo_legal_title: "Mentions légales — Chibi Kokoro | Restaurant Japonais Barcelone",
  seo_legal_desc: "Mentions légales de CHIBIKOKORO S.L.",
  seo_privacy_title: "Politique de confidentialité — Chibi Kokoro | RGPD",
  seo_privacy_desc: "Politique de confidentialité de CHIBIKOKORO S.L. conforme au RGPD.",
  seo_cookies_title: "Politique de cookies — Chibi Kokoro",
  seo_cookies_desc: "Politique de cookies de Chibi Kokoro.",
};

const it: LegalTranslationKeys = {
  cookie_banner_title: "Utilizziamo i cookie",
  cookie_banner_desc: "Utilizziamo i cookie per migliorare la tua esperienza. Puoi accettare tutti, rifiutare quelli non essenziali o configurare le tue preferenze.",
  cookie_accept_all: "Accetta tutti",
  cookie_reject: "Rifiuta",
  cookie_configure: "Configura",
  cookie_settings_title: "Impostazioni cookie",
  cookie_technical: "Cookie tecnici",
  cookie_technical_desc: "Necessari per il funzionamento di base del sito. Non possono essere disattivati.",
  cookie_analytical: "Cookie analitici",
  cookie_analytical_desc: "Ci consentono di analizzare l'uso del sito per migliorarne le prestazioni.",
  cookie_advertising: "Cookie pubblicitari",
  cookie_advertising_desc: "Utilizzati per mostrare annunci pertinenti in base ai tuoi interessi.",
  cookie_always_active: "Sempre attivi",
  cookie_save: "Salva preferenze",
  cookie_manage: "Impostazioni cookie",
  footer_legal_notice: "Avviso legale",
  footer_privacy_policy: "Informativa sulla privacy",
  footer_cookie_policy: "Politica dei cookie",
  seo_legal_title: "Avviso legale — Chibi Kokoro | Ristorante Giapponese Barcellona",
  seo_legal_desc: "Avviso legale di CHIBIKOKORO S.L.",
  seo_privacy_title: "Informativa sulla privacy — Chibi Kokoro | GDPR",
  seo_privacy_desc: "Informativa sulla privacy di CHIBIKOKORO S.L. conforme al GDPR.",
  seo_cookies_title: "Politica dei cookie — Chibi Kokoro",
  seo_cookies_desc: "Politica dei cookie di Chibi Kokoro.",
};

const de: LegalTranslationKeys = {
  cookie_banner_title: "Wir verwenden Cookies",
  cookie_banner_desc: "Wir verwenden Cookies, um Ihr Erlebnis zu verbessern. Sie können alle akzeptieren, nicht wesentliche ablehnen oder Ihre Präferenzen konfigurieren.",
  cookie_accept_all: "Alle akzeptieren",
  cookie_reject: "Ablehnen",
  cookie_configure: "Konfigurieren",
  cookie_settings_title: "Cookie-Einstellungen",
  cookie_technical: "Technische Cookies",
  cookie_technical_desc: "Notwendig für den grundlegenden Betrieb der Website. Können nicht deaktiviert werden.",
  cookie_analytical: "Analytische Cookies",
  cookie_analytical_desc: "Ermöglichen die Analyse der Websitenutzung zur Verbesserung von Leistung und Inhalt.",
  cookie_advertising: "Werbe-Cookies",
  cookie_advertising_desc: "Werden verwendet, um relevante Werbung basierend auf Ihren Interessen anzuzeigen.",
  cookie_always_active: "Immer aktiv",
  cookie_save: "Einstellungen speichern",
  cookie_manage: "Cookie-Einstellungen",
  footer_legal_notice: "Impressum",
  footer_privacy_policy: "Datenschutzrichtlinie",
  footer_cookie_policy: "Cookie-Richtlinie",
  seo_legal_title: "Impressum — Chibi Kokoro | Japanisches Restaurant Barcelona",
  seo_legal_desc: "Impressum von CHIBIKOKORO S.L.",
  seo_privacy_title: "Datenschutzrichtlinie — Chibi Kokoro | DSGVO",
  seo_privacy_desc: "Datenschutzrichtlinie von CHIBIKOKORO S.L. gemäß DSGVO.",
  seo_cookies_title: "Cookie-Richtlinie — Chibi Kokoro",
  seo_cookies_desc: "Cookie-Richtlinie von Chibi Kokoro.",
};

const ru: LegalTranslationKeys = {
  cookie_banner_title: "Мы используем cookies",
  cookie_banner_desc: "Мы используем файлы cookie для улучшения вашего опыта. Вы можете принять все, отклонить необязательные или настроить предпочтения.",
  cookie_accept_all: "Принять все",
  cookie_reject: "Отклонить",
  cookie_configure: "Настроить",
  cookie_settings_title: "Настройки cookies",
  cookie_technical: "Технические cookies",
  cookie_technical_desc: "Необходимы для базового функционирования сайта. Не могут быть отключены.",
  cookie_analytical: "Аналитические cookies",
  cookie_analytical_desc: "Позволяют анализировать использование сайта для улучшения производительности.",
  cookie_advertising: "Рекламные cookies",
  cookie_advertising_desc: "Используются для показа релевантной рекламы на основе ваших интересов.",
  cookie_always_active: "Всегда активны",
  cookie_save: "Сохранить настройки",
  cookie_manage: "Настройки cookies",
  footer_legal_notice: "Юридическая информация",
  footer_privacy_policy: "Политика конфиденциальности",
  footer_cookie_policy: "Политика cookies",
  seo_legal_title: "Юридическая информация — Chibi Kokoro | Японский ресторан Барселона",
  seo_legal_desc: "Юридическая информация CHIBIKOKORO S.L.",
  seo_privacy_title: "Политика конфиденциальности — Chibi Kokoro | GDPR",
  seo_privacy_desc: "Политика конфиденциальности CHIBIKOKORO S.L. в соответствии с GDPR.",
  seo_cookies_title: "Политика cookies — Chibi Kokoro",
  seo_cookies_desc: "Политика cookies Chibi Kokoro.",
};

export const legalTranslations: Record<Language, LegalTranslationKeys> = { es, en, ca, pt, fr, it, de, ru };
